/**
 * PartnerPropertyPage — the property a partner's link opens, and what the link
 * may do with it.
 *
 * The viewer has no Orchard login. The link token is redeemed for a session
 * scoped to one property, then the property, the link subject's own offer and
 * its questions are loaded as one view. The rail beside the property holds a
 * card for each thing the link can act on, and nothing for what it cannot.
 */

import { useState, type ReactNode } from "react";
import { Redact, REDACT_ATTRS } from "@/common/analytics";
import { Button } from "@/common/components/button";
import { Callout } from "@/common/components/callout";
import { LoadingSpinner } from "@/common/components/loading-spinner";
import { Text } from "@/common/components/text";
import type { PropertyMediaResponse } from "@/features/properties/api/properties";
import { PropertyConditionSection } from "@/features/properties/components/PropertyConditionSection";
import { PropertyImageGallery } from "@/features/properties/components/PropertyImageGallery";
import { PropertyPricingGrid } from "@/features/properties/components/PropertyPricingGrid";
import {
  PropertyStatItem,
  PropertyStatsGrid,
} from "@/features/properties/components/PropertyStatsGrid";
import * as statusStyles from "@/features/properties/components/PropertyStatusBadge/PropertyStatusBadge.css";
import { STATUS_CONFIG } from "@/features/properties/components/PropertyStatusBadge/propertyStatusUtils";
import {
  CLOSING_TIMELINE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
} from "@/features/properties/constants";
import { formatSubmittedAgo } from "@/features/properties/utils/submittedAgo";
import {
  PARTNER_CAPABILITIES,
  PartnerLinkError,
  type PartnerLinkFailure,
  type PartnerProperty,
} from "../../api/partnerLinks";
import {
  usePartnerLink,
  type PartnerLinkView,
} from "../../hooks/usePartnerLink";
import type { PartnerLinkSession } from "../../hooks/usePartnerMutation";
import { MakeOfferCard } from "../MakeOfferCard/MakeOfferCard";
import { QuestionsCard } from "../QuestionsCard/QuestionsCard";
import { YourOfferCard } from "../YourOfferCard/YourOfferCard";
import * as styles from "./PartnerPropertyPage.css";

interface PartnerPropertyPageProps {
  /** Link token taken from the URL; empty when the URL had none. */
  token: string;
  slug: string;
  propertyId: string;
  /**
   * Which card starts with its form revealed. Only the design harness passes
   * this, to capture the revealed states without a click.
   */
  initialReveal?: PartnerPageReveal;
}

export type PartnerPageReveal = "offer" | "question";

const FAILURE_COPY: Record<
  PartnerLinkFailure,
  { title: string; message: string }
> = {
  invalid: {
    title: "Link not valid",
    message: "This link is no longer valid. Ask the partner for a new one.",
  },
  unavailable: {
    title: "Property no longer available",
    message: "This property is no longer accepting offers.",
  },
  temporary: {
    title: "Something went wrong",
    message:
      "We couldn't load this property. Check your connection and try again.",
  },
};

const dateFormat = new Intl.DateTimeFormat("en-US", { dateStyle: "long" });

function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

/** Format a calendar date (`YYYY-MM-DD`) without shifting it across time zones. */
function formatCalendarDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return dateFormat.format(new Date(year, month - 1, day));
}

function formatAddress(address: NonNullable<PartnerProperty["address"]>): {
  line1: string;
  line2: string;
} {
  return {
    line1: address.unit_number
      ? `${address.street_address} ${address.unit_number}`
      : address.street_address,
    line2: `${address.city}, ${address.state} ${address.zipcode}`,
  };
}

function failureOf(error: Error): PartnerLinkFailure {
  return error instanceof PartnerLinkError ? error.failure : "temporary";
}

/** Offer statuses that keep "Make an offer" away: one open offer per link. */
const OPEN_OFFER_STATUSES: ReadonlySet<string> = new Set([
  "pending",
  "engaged",
  "accepted",
]);

export function PartnerPropertyPage({
  token,
  slug,
  propertyId,
  initialReveal,
}: PartnerPropertyPageProps) {
  const query = usePartnerLink(token, slug, propertyId);
  // Held here, above the rail, so the reload a refusal triggers does not clear it.
  const [staleNotice, setStaleNotice] = useState(false);

  // A reload that finds the link invalid or the property gone replaces the
  // page, as a fresh open would. Any other failed reload keeps what is shown.
  const lostLink = query.isError && failureOf(query.error) !== "temporary";

  let body: ReactNode = (
    <div className={styles.statusCard} role="status">
      <LoadingSpinner size="md" label="Loading property…" />
    </div>
  );
  if (query.data && !lostLink) {
    const session: PartnerLinkSession = {
      token,
      slug,
      pathPropertyId: propertyId,
      onStale: () => setStaleNotice(true),
    };
    body = (
      <PropertyDetails
        view={query.data}
        session={session}
        initialReveal={initialReveal}
        notices={
          <>
            {staleNotice && (
              <Callout
                color="caution"
                dismissible
                onDismiss={() => setStaleNotice(false)}
              >
                This page was out of date, so it has been refreshed. Check the
                latest status before trying again.
              </Callout>
            )}
            {query.isRefetchError && (
              <Callout color="caution">
                We couldn&apos;t refresh this page.{" "}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={query.isFetching}
                  onClick={() => void query.refetch()}
                >
                  Try again
                </Button>
              </Callout>
            )}
          </>
        }
      />
    );
  } else if (query.isError && (lostLink || !query.isFetching)) {
    const failure = failureOf(query.error);
    const copy = FAILURE_COPY[failure];
    body = (
      <div className={styles.statusCard} role="alert">
        <Text variant="bodyLg" weight="bold" align="center">
          {copy.title}
        </Text>
        <Text variant="bodySm" color="muted" align="center">
          {copy.message}
        </Text>
        {failure === "temporary" && (
          <Button
            type="button"
            width="full"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        )}
      </div>
    );
  }

  return (
    // The public layout has no redaction baseline, and this page shows offer
    // prices, private notes and questions: the same pair the signed-in layout sets.
    <main
      className={styles.page}
      data-heap-redact-text="true"
      data-heap-redact-attributes={REDACT_ATTRS}
    >
      <div className={styles.content}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Cash Offer"
          className={styles.logo}
        />
        {body}
      </div>
    </main>
  );
}

/**
 * Adapt the partner payload's media to what PropertyImageGallery expects.
 *
 * The gallery is the same component the signed-in property page uses, so a
 * partner gets the same viewer rather than a second-class thumbnail strip. The
 * partner response carries only the URLs and media type, because the rest say
 * nothing a partner needs and the allowlist keeps them out.
 *
 * What it fills in is safe to fill in: the endpoint returns READY media only,
 * in display order, so `status` and `display_order` follow from the payload
 * rather than standing in for withheld facts. `id` exists because the gallery
 * keys on it.
 */
function toGalleryMedia(
  media: PartnerProperty["media"],
): PropertyMediaResponse[] {
  return media.map((item, index) => ({
    id: `partner-media-${index}`,
    property_id: "",
    media_type: item.media_type,
    file_url: item.url,
    file_size: 0,
    display_order: index,
    uploaded_at: "",
    status: "ready",
    thumbnail_url: item.thumbnail_url,
    processed_at: null,
    error_message: null,
    caption: null,
  }));
}

/**
 * The property's status in the words the marketplace uses.
 *
 * Reuses STATUS_CONFIG so a partner reads "Actively reviewing offers" rather
 * than "available" — the same phrase the listing agent sees. The interactive
 * PropertyStatusBadge cannot come with it: it mutates status and reads the
 * signed-in user, neither of which exists here.
 */
function StatusIndicator({ status }: { status: string }) {
  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
  if (!config) {
    return null;
  }
  return (
    <span className={statusStyles.statusIndicator}>
      <span
        className={statusStyles.statusDot}
        data-color={config.color}
        aria-hidden="true"
      />
      <span className={statusStyles.statusText} data-color={config.color}>
        {config.label}
      </span>
    </span>
  );
}

function PropertyDetails({
  view,
  session,
  initialReveal,
  notices,
}: {
  view: PartnerLinkView;
  session: PartnerLinkSession;
  initialReveal?: PartnerPageReveal;
  notices: ReactNode;
}) {
  const { property, offer, questions, capabilities } = view;
  const isAvailable = property.status === "available";
  const showMakeOffer =
    capabilities.includes(PARTNER_CAPABILITIES.offerCreate) &&
    isAvailable &&
    !(offer && OPEN_OFFER_STATUSES.has(offer.status));
  const showQuestions =
    capabilities.includes(PARTNER_CAPABILITIES.propertyRequestInfo) &&
    (isAvailable || questions.length > 0);
  const address = property.address ? formatAddress(property.address) : null;
  const listingStats = [
    {
      icon: "home",
      label: "PROPERTY TYPE",
      value: optionLabel(PROPERTY_TYPE_OPTIONS, property.property_type),
    },
    property.closing_timeline !== null && {
      icon: "schedule",
      label: "CLOSING TIMELINE",
      value: optionLabel(CLOSING_TIMELINE_OPTIONS, property.closing_timeline),
    },
    property.offer_deadline !== null && {
      icon: "event",
      label: "OFFER DEADLINE",
      value: formatCalendarDate(property.offer_deadline),
    },
  ].filter((stat) => stat !== false);

  return (
    <>
      {/* Above both columns: whatever a partner is being asked to decide sits
          beside this, not below the whole property. */}
      <header className={styles.heading}>
        {/* The address and prices are personal data: fence them off from analytics. */}
        <Redact as="div">
          <Text as="h1" variant="h3">
            {address ? address.line1 : "Property details"}
          </Text>
          {address && (
            <Text variant="body" color="muted">
              {address.line2}
            </Text>
          )}
        </Redact>
        {property.submitted_at && (
          <Text variant="bodySm" color="muted">
            {formatSubmittedAgo(property.submitted_at)}
          </Text>
        )}
        <StatusIndicator status={property.status} />
      </header>

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <article className={styles.card}>
            {property.media.length > 0 && (
              <PropertyImageGallery media={toGalleryMedia(property.media)} />
            )}

            {/* An unshared price is null and left out, never shown as a dash. */}
            <PropertyPricingGrid property={property} hideMissingPrices />

            <PropertyStatsGrid property={property} />

            {/* Outside the stats grid: a property type or a date needs more
                width than a bed count. */}
            <div className={styles.listingStats}>
              {listingStats.map((stat) => (
                <PropertyStatItem key={stat.label} {...stat} />
              ))}
            </div>

            {/* The marketplace's own condition section, from the marketplace's own
          component. It renders nothing when no condition was recorded. */}
            <PropertyConditionSection
              property={property}
              className={styles.section}
            />

            {property.additional_notes && (
              <section className={styles.section}>
                <Text as="h2" variant="body" weight="bold">
                  Additional notes
                </Text>
                <Text variant="body" color="muted" className={styles.notes}>
                  {property.additional_notes}
                </Text>
              </section>
            )}

          </article>
        </div>

        <aside className={styles.rail} aria-label="Actions">
          {notices}
          {/* A closed offer stays above the returned "Make an offer" card. */}
          {offer && (
            <YourOfferCard
              session={session}
              offer={offer}
              property={property}
              capabilities={capabilities}
            />
          )}
          {showMakeOffer && (
            <MakeOfferCard
              session={session}
              propertyId={property.id}
              initialOpen={initialReveal === "offer"}
            />
          )}
          {showQuestions && (
            <QuestionsCard
              session={session}
              propertyId={property.id}
              questions={questions}
              canAsk={isAvailable}
              initialOpen={initialReveal === "question"}
            />
          )}
        </aside>
      </div>
    </>
  );
}
